import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import './PostAmazonPayments.css';

const PostAmazonPayments = () => {
  const [settlementReports, setSettlementReports] = useState([]);
  const [selectedSettlementId, setSelectedSettlementId] = useState(null);
  const [pivotData, setPivotData] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]); // Changed to array for multiple entries
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPivot, setIsLoadingPivot] = useState(false);
  const [isLoadingJournal, setIsLoadingJournal] = useState(false);
  const [isSubmittingToQBO, setIsSubmittingToQBO] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Load settlement reports on component mount
  useEffect(() => {
    loadSettlementReports();
  }, []);



  const loadSettlementReports = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiCall('/api/post-amazon-payments/settlement-reports', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettlementReports(data.reports || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load settlement reports');
      }
    } catch (error) {
      console.error('Error loading settlement reports:', error);
      setError('Error loading settlement reports. Please check the console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPivotData = async (settlementId) => {
    try {
      setIsLoadingPivot(true);
      setError(null);
      
      const response = await apiCall(`/api/post-amazon-payments/pivot-data/${settlementId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPivotData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load pivot data');
      }
    } catch (error) {
      console.error('Error loading pivot data:', error);
      setError('Error loading pivot data. Please check the console for details.');
    } finally {
      setIsLoadingPivot(false);
    }
  };

  const loadJournalEntries = async (settlementId) => {
    try {
      setIsLoadingJournal(true);
      setError(null);
      
      const response = await apiCall(`/api/post-amazon-payments/journal-entries/${settlementId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setJournalEntries(data.journal_entries || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load journal entries');
      }
    } catch (error) {
      console.error('Error loading journal entries:', error);
      setError('Error loading journal entries. Please check the console for details.');
    } finally {
      setIsLoadingJournal(false);
    }
  };

  const handleSettlementSelect = (settlementId) => {
    // Only clear data if we're actually changing to a different settlement
    if (selectedSettlementId !== settlementId) {
      setSelectedSettlementId(settlementId);
      setPivotData(null);
      setJournalEntries([]);
      loadPivotData(settlementId);
      loadJournalEntries(settlementId);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Parse the date string manually to avoid timezone issues
      // Expected format: "2025-05-20"
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1; // Month is 0-indexed in JavaScript
        const day = parseInt(parts[2]);
        
        // Create date in local timezone without time component
        const date = new Date(year, month, day);
        
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } else {
        // Fallback to original method
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const submitToQBO = async (settlementId, journalEntryIndex = null) => {
    try {
      setIsSubmittingToQBO(true);
      setError(null);
      setSuccessMessage(null);
      
      // If journalEntryIndex is provided, submit specific entry, otherwise submit all
      const endpoint = journalEntryIndex !== null 
        ? `/api/post-amazon-payments/submit-to-qbo/${settlementId}/${journalEntryIndex}`
        : `/api/post-amazon-payments/submit-to-qbo/${settlementId}`;
      
      const response = await apiCall(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(data.message);
        
        if (journalEntryIndex !== null) {
          // Single entry submitted
          alert(`✅ ${data.message}\n\nQBO ID: ${data.qbo_id}\nSettlement ID: ${data.settlement_id}\nJournal Entry: ${journalEntryIndex + 1}\nLines: ${data.lines_count}\nTotal Amount: ${formatCurrency(data.total_amount)}`);
        } else {
          // All entries submitted
          alert(`✅ ${data.message}\n\nSettlement ID: ${data.settlement_id}\nTotal Journal Entries: ${data.total_entries}\nTotal Lines: ${data.total_lines}\nTotal Amount: ${formatCurrency(data.total_amount)}`);
        }
      } else {
        const errorData = await response.json();
        const errorMsg = errorData.error || 'Failed to submit journal entry to QBO';
        setError(errorMsg);
        alert(`❌ ${errorMsg}`);
      }
    } catch (error) {
      console.error('Error submitting to QBO:', error);
      const errorMsg = 'Network error occurred while submitting to QBO';
      setError(errorMsg);
      alert(`❌ ${errorMsg}`);
    } finally {
      setIsSubmittingToQBO(false);
    }
  };

  // Helper function to check if settlement spans multiple months
  const getSettlementMonthInfo = (settlementReport) => {
    if (!settlementReport) return null;
    
    const startDate = new Date(settlementReport.settlement_start_date);
    const endDate = new Date(settlementReport.settlement_end_date);
    
    const startMonth = startDate.getMonth();
    const startYear = startDate.getFullYear();
    const endMonth = endDate.getMonth();
    const endYear = endDate.getFullYear();
    
    const spansMultipleMonths = (startMonth !== endMonth) || (startYear !== endYear);
    
    if (spansMultipleMonths) {
      // Find the end of the first month (last day of start_month)
      let firstMonthEnd, secondMonthStart;
      
      if (startMonth === 11) { // December
        firstMonthEnd = new Date(startYear, 11, 31);
        secondMonthStart = new Date(startYear + 1, 0, 1);
      } else {
        // For other months: first day of next month minus 1 day = last day of current month
        firstMonthEnd = new Date(startYear, startMonth + 1, 0);
        secondMonthStart = new Date(startYear, startMonth + 1, 1);
      }
      
      return {
        spansMultipleMonths: true,
        firstPeriod: {
          start: settlementReport.settlement_start_date,
          end: firstMonthEnd.toISOString().split('T')[0]
        },
        secondPeriod: {
          start: secondMonthStart.toISOString().split('T')[0],
          end: settlementReport.settlement_end_date
        }
      };
    }
    
    return {
      spansMultipleMonths: false,
      singlePeriod: {
        start: settlementReport.settlement_start_date,
        end: settlementReport.settlement_end_date
      }
    };
  };

  // Get the selected settlement report object
  const selectedSettlementReport = settlementReports.find(report => report.settlement_id === selectedSettlementId);
  const monthInfo = getSettlementMonthInfo(selectedSettlementReport);

  return (
    <div className="post-amazon-payments">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Post Amazon Payments to QBO</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Select a settlement report to analyze payment data</li>
              <li>Review the pivot table of transactions by type</li>
              <li>Prepare and post journal entries to QuickBooks Online</li>
              <li>Multi-month settlements will create separate journal entries for each month</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        {/* Settlement Reports Section */}
        <div className="section">
          <h2>📊 Available Settlement Reports</h2>
          {isLoading ? (
            <div className="loading">Loading settlement reports...</div>
          ) : error ? (
            <div className="error">❌ {error}</div>
          ) : settlementReports.length > 0 ? (
            <div className="settlement-reports-list">
              <h3>Found {settlementReports.length} settlement reports:</h3>
              {settlementReports.map((report, index) => {
                const reportMonthInfo = getSettlementMonthInfo(report);
                return (
                  <div 
                    key={report.settlement_id} 
                    className={`settlement-report-item ${selectedSettlementId === report.settlement_id ? 'selected' : ''}`}
                    onClick={() => handleSettlementSelect(report.settlement_id)}
                  >
                    <div className="report-header">
                      <h4>Settlement {index + 1}: {report.settlement_id}</h4>
                      {reportMonthInfo.spansMultipleMonths && (
                        <span className="multi-month-badge">📅 Multi-Month</span>
                      )}
                    </div>
                    <div className="report-details">
                      <div className="detail-row">
                        <strong>Period:</strong> {formatDate(report.settlement_start_date)} to {formatDate(report.settlement_end_date)}
                      </div>
                      <div className="detail-row">
                        <strong>Total Records:</strong> {report.total_records?.toLocaleString() || '0'}
                      </div>
                      {reportMonthInfo.spansMultipleMonths && (
                        <div className="detail-row month-breakdown">
                          <strong>Month Breakdown:</strong>
                          <div className="month-periods">
                            <div>• {formatDate(reportMonthInfo.firstPeriod.start)} - {formatDate(reportMonthInfo.firstPeriod.end)} (end of {new Date(reportMonthInfo.firstPeriod.start).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})</div>
                            <div>• {formatDate(reportMonthInfo.secondPeriod.start)} - {formatDate(reportMonthInfo.secondPeriod.end)} (start of {new Date(reportMonthInfo.secondPeriod.start).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})</div>
                          </div>
                          <div className="multi-month-notice">
                            <em>Note: Actual transaction date ranges will be calculated when you select this settlement</em>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-reports">
              No settlement reports found in the database.
            </div>
          )}
        </div>

        {/* Pivot Table Section */}
        {selectedSettlementId && (
          <div className="section">
            <h2>📋 Payment Analysis - Settlement {selectedSettlementId}</h2>
            {monthInfo && monthInfo.spansMultipleMonths && (
              <div className="multi-month-notice">
                <strong>📅 Multi-Month Settlement:</strong> This settlement spans from {formatDate(monthInfo.firstPeriod.start)} to {formatDate(monthInfo.secondPeriod.end)}. 
                The analysis table shows the complete data, but journal entries will be split by month.
              </div>
            )}
            {isLoadingPivot ? (
              <div className="loading">Loading pivot data...</div>
            ) : error ? (
              <div className="error">❌ {error}</div>
            ) : pivotData ? (
              <div className="pivot-table-container">
                <table className="pivot-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      {pivotData.columns.map(column => (
                        <th key={column}>{column}</th>
                      ))}
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Price Types */}
                    {pivotData.price_types.map(row => (
                      <tr key={`price_${row.category}`}>
                        <td className="category-cell">{row.category}</td>
                        {pivotData.columns.map(column => (
                          <td key={column} className="amount-cell">
                            {formatCurrency(row.amounts[column] || 0)}
                          </td>
                        ))}
                        <td className="total-cell">{formatCurrency(row.total)}</td>
                      </tr>
                    ))}
                    
                    {/* Promotion Types */}
                    {pivotData.promotion_types.map(row => (
                      <tr key={`promo_${row.category}`}>
                        <td className="category-cell">{row.category}</td>
                        {pivotData.columns.map(column => (
                          <td key={column} className="amount-cell">
                            {formatCurrency(row.amounts[column] || 0)}
                          </td>
                        ))}
                        <td className="total-cell">{formatCurrency(row.total)}</td>
                      </tr>
                    ))}
                    
                    {/* Order Fee Types */}
                    {pivotData.order_fee_types.map(row => (
                      <tr key={`order_fee_${row.category}`}>
                        <td className="category-cell">{row.category}</td>
                        {pivotData.columns.map(column => (
                          <td key={column} className="amount-cell">
                            {formatCurrency(row.amounts[column] || 0)}
                          </td>
                        ))}
                        <td className="total-cell">{formatCurrency(row.total)}</td>
                      </tr>
                    ))}
                    
                    {/* Item Related Fee Types */}
                    {pivotData.item_related_fee_types.map(row => (
                      <tr key={`item_fee_${row.category}`}>
                        <td className="category-cell">{row.category}</td>
                        {pivotData.columns.map(column => (
                          <td key={column} className="amount-cell">
                            {formatCurrency(row.amounts[column] || 0)}
                          </td>
                        ))}
                        <td className="total-cell">{formatCurrency(row.total)}</td>
                      </tr>
                    ))}
                    
                    {/* Shipment Fee Types */}
                    {pivotData.shipment_fee_types.map(row => (
                      <tr key={`shipment_fee_${row.category}`}>
                        <td className="category-cell">{row.category}</td>
                        {pivotData.columns.map(column => (
                          <td key={column} className="amount-cell">
                            {formatCurrency(row.amounts[column] || 0)}
                          </td>
                        ))}
                        <td className="total-cell">{formatCurrency(row.total)}</td>
                      </tr>
                    ))}
                    
                    {/* Miscellaneous Fees */}
                    <tr className="misc-fees-row">
                      <td className="category-cell">Miscellaneous Fees</td>
                      {pivotData.columns.map(column => (
                        <td key={column} className="amount-cell">
                          {formatCurrency(pivotData.misc_fees[column] || 0)}
                        </td>
                      ))}
                      <td className="total-cell">{formatCurrency(pivotData.misc_fees.total)}</td>
                    </tr>
                    
                    {/* Other Fees */}
                    <tr className="other-fees-row">
                      <td className="category-cell">Other Fees</td>
                      {pivotData.columns.map(column => (
                        <td key={column} className="amount-cell">
                          {formatCurrency(pivotData.other_fees[column] || 0)}
                        </td>
                      ))}
                      <td className="total-cell">{formatCurrency(pivotData.other_fees.total)}</td>
                    </tr>
                    
                    {/* Direct Payment Types */}
                    {pivotData.direct_payment_types.map(row => (
                      <tr key={`direct_${row.category}`}>
                        <td className="category-cell">{row.category}</td>
                        {pivotData.columns.map(column => (
                          <td key={column} className="amount-cell">
                            {formatCurrency(row.amounts[column] || 0)}
                          </td>
                        ))}
                        <td className="total-cell">{formatCurrency(row.total)}</td>
                      </tr>
                    ))}
                    
                    {/* Other Amount */}
                    <tr className="other-amount-row">
                      <td className="category-cell">Other Amount</td>
                      {pivotData.columns.map(column => (
                        <td key={column} className="amount-cell">
                          {formatCurrency(pivotData.other_amount[column] || 0)}
                        </td>
                      ))}
                      <td className="total-cell">{formatCurrency(pivotData.other_amount.total)}</td>
                    </tr>
                    
                    {/* Grand Total */}
                    <tr className="grand-total-row">
                      <td className="category-cell"><strong>GRAND TOTAL</strong></td>
                      {pivotData.columns.map(column => (
                        <td key={column} className="amount-cell">
                          <strong>{formatCurrency(pivotData.grand_totals[column] || 0)}</strong>
                        </td>
                      ))}
                      <td className="total-cell">
                        <strong>{formatCurrency(pivotData.grand_totals.total)}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-data">No pivot data available for this settlement.</div>
            )}
          </div>
        )}

        {/* Journal Entries Section */}
        {selectedSettlementId && (
          <div className="section">
            <h2>📝 Journal Entry Preview{journalEntries.length > 1 ? 's' : ''} - Settlement {selectedSettlementId}</h2>
            {journalEntries.length > 1 && (
              <div className="multi-month-notice">
                <strong>Multi-Month Settlement Detected:</strong> This settlement spans multiple months and has been split into separate journal entries based on actual transaction dates.
                <br />
                <strong>Actual Transaction Date Ranges:</strong>
                <br />
                • <strong>First Period:</strong> {journalEntries[0]?.period_start ? formatDate(journalEntries[0].period_start) : 'N/A'} to {journalEntries[0]?.period_end ? formatDate(journalEntries[0].period_end) : 'N/A'}
                <br />
                • <strong>Second Period:</strong> {journalEntries[1]?.period_start ? formatDate(journalEntries[1].period_start) : 'N/A'} to {journalEntries[1]?.period_end ? formatDate(journalEntries[1].period_end) : 'N/A'}
              </div>
            )}
            {isLoadingJournal ? (
              <div className="loading">Loading journal entries...</div>
            ) : error ? (
              <div className="error">❌ {error}</div>
            ) : successMessage ? (
              <div className="success">✅ {successMessage}</div>
            ) : journalEntries.length > 0 ? (
              <div className="journal-entries-container">
                {journalEntries.map((journalEntry, index) => {
                  return (
                    <div key={index} className="journal-entry-container">
                      <div className="journal-entry-header">
                        <h3>
                          Journal Entry {index + 1}
                          {journalEntries.length > 1 && (
                            <span className="period-info">
                              ({formatDate(journalEntry.period_start)} - {formatDate(journalEntry.period_end)})
                            </span>
                          )}
                        </h3>
                      </div>
                      
                      <div className="journal-entry-summary">
                        <div className="summary-stats">
                          <div className="stat-item">
                            <strong>Total Credits:</strong> {formatCurrency(journalEntry.totals.credits)}
                          </div>
                          <div className="stat-item">
                            <strong>Total Debits:</strong> {formatCurrency(journalEntry.totals.debits)}
                          </div>
                          <div className="stat-item">
                            <strong>Balance:</strong> {formatCurrency(journalEntry.totals.balance)}
                          </div>
                        </div>
                      </div>
                      
                      <table className="journal-entry-table">
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Received From</th>
                            <th>Account</th>
                            <th>Description</th>
                            <th>Type</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {journalEntry.journal_entry.map((row) => (
                            <tr key={row.row_number} className={`journal-row ${row.type}`}>
                              <td className="row-number">{row.row_number}</td>
                              <td className="received-from">{row.received_from}</td>
                              <td className="account">{row.account}</td>
                              <td className="description">{row.description}</td>
                              <td className="type">
                                <span className={`type-badge ${row.type}`}>
                                  {row.type.toUpperCase()}
                                </span>
                              </td>
                              <td className="amount">{formatCurrency(row.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      <div className="journal-entry-actions">
                        <button 
                          className="btn btn-primary"
                          onClick={() => submitToQBO(selectedSettlementId, index)}
                          disabled={isSubmittingToQBO}
                        >
                          {isSubmittingToQBO ? '⏳ Submitting...' : `📤 Submit Journal Entry ${index + 1} to QBO`}
                        </button>
                        <button className="btn btn-secondary">
                          📄 Export as PDF
                        </button>
                      </div>
                    </div>
                  );
                })}
                
                {/* Submit All Button for Multi-Month Settlements */}
                {journalEntries.length > 1 && (
                  <div className="submit-all-container">
                    <div className="submit-all-notice">
                      <strong>💡 Tip:</strong> You can submit each journal entry individually above, or submit all at once below.
                    </div>
                    <button 
                      className="btn btn-success btn-large"
                      onClick={() => submitToQBO(selectedSettlementId)}
                      disabled={isSubmittingToQBO}
                    >
                      {isSubmittingToQBO ? '⏳ Submitting All...' : '🚀 Submit All Journal Entries to QBO'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="no-data">No journal entry data available for this settlement.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PostAmazonPayments; 